import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private platformId = inject<string>(PLATFORM_ID);
  private cookieService = inject(CookieService);

  public get = <T>(key: string): T => {
    if (isPlatformServer(this.platformId)) return;
    try {
      let string = sessionStorage.getItem(key);
      if (!string) string = localStorage.getItem(key);
      if (string === 'undefined') return undefined;
      return string ? JSON.parse(string) : undefined;
    } catch (err) {
      console.warn(err);
    }
  };

  public has = (key: string) => {
    try {
      if (isPlatformServer(this.platformId)) return;
      let string = sessionStorage.getItem(key);
      if (!string) string = localStorage.getItem(key);
      return !!string;
    } catch (err) {
      console.warn(err);
    }
  };

  public set = (key: string, data: unknown, session = false) => {
    try {
      if (isPlatformServer(this.platformId)) return;
      if (session) sessionStorage.setItem(key, JSON.stringify(data));
      else localStorage.setItem(key, JSON.stringify(data));
    } catch (err) {
      console.warn(err);
    }
  };

  public remove = (key: string) => {
    try {
      if (isPlatformServer(this.platformId)) return;
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (err) {
      console.warn(err);
    }
  };

  private getDomain(customDomain: string, correctPicflowUrl: string): string {
    if (customDomain) return customDomain;
    return correctPicflowUrl.split('://')[1].split(':')[0];
  }

  public setCookie = (key: string, data: unknown, customDomain: string, correctPicflowUrl: string) => {
    if (isPlatformServer(this.platformId)) return;

    this.cookieService.set(`PICFLOW_${key}`, JSON.stringify(data), {
      expires: 365,
      sameSite: 'Lax',
      domain: '.' + this.getDomain(customDomain, correctPicflowUrl),
      path: '/',
    });
  };

  public getCookie = (key: string) => {
    if (isPlatformServer(this.platformId)) return;
    try {
      const cookieVal = this.cookieService.get(`PICFLOW_${key}`);
      if (!cookieVal) return;
      const resp = JSON.parse(cookieVal);
      return resp;
    } catch (err) {
      console.error('getCookie error', key, err);
    }
  };

  public removeCookie = (key: string, customDomain: string, correctPicflowUrl: string) => {
    if (isPlatformServer(this.platformId)) return;
    this.cookieService.delete(`PICFLOW_${key}`, '/', this.getDomain(customDomain, correctPicflowUrl));
  };

  public getAny = (key: string) => {
    return this.get(key) || this.getCookie(key);
  };

  public hasAny = (key: string) => {
    return this.has(key) || !!this.getCookie(key);
  };
}
